import {LFO, ENVELOPE} from 'constants/mod-types.js';
import StepData from "pattern/StepData.js";
import {patternSwitch} from './pattern.js';
import {publish} from 'messaging/queue.js';
import {
    MESSAGE,
    ON,
    PARALLEL,
    FILTER,
    SWITCH_OUTPUT,
    CHOKE,
    SET,
    PATTERN_SWITCH,
    GRAB,
    GRAB_TIME,
    lazy,
    SEQUENTIALLY,
    TRIGGER,
    COND,
    SET_MODULATOR,
    SET_MACRO,
} from './lang.js';

const DEFAULT_STEP = new StepData();

export const _eval = (op, t, effect) => {
    let ret = op; 
    if (op.axiom === undefined) {
        // we need to execute the operation
        ret = op(t.stepData, t.stepNumber, t.time, effect, t.graphics, t.delays);
    }

    // otherwise, the passed op was already reduced to axioms
    // anyway, at this point we see what axiom it is, and execute
    // the axiom accoringly

    let output = [];
    let copied;
    switch (ret.axiom) {
    case FILTER:
        if (lazy(ret.value, t.stepData, t.stepNumber, t.time, effect)) {
            return [t];
        }
        return [];
    case SET:
        copied = effect.copyTrigger(t, true);
        copied.stepData.slide = t.stepData.slide;
        let lazily = lazy(ret.value, t.stepData, t.stepNumber, t.time, effect);
        if (Array.isArray(lazily)) {
            lazily = lazily[0];
        }
        let _value = lazily[ret.param] !== undefined ?
            lazily[ret.param] :
            typeof lazily === "function" ? lazy(lazily, t.stepData, t.stepNumber, t.time, effect) : lazily;
        if (typeof _value === "object") {
            // shitty reason alt transforms to {axiom: SEQUENTIALLY, sequence: [...alternateOptions]}
            // we need to get the first one
            if (_value.sequence) {
                _value = _value.sequence[0];
            }
        }
        let _param = copied.stepData[ret.param + "Parameter"];
        if (_param) {
            _param.set(_value);
        }
        return [copied];
    case SWITCH_OUTPUT:
        copied = effect.copyTrigger(t, true);
        copied.outputNumber = lazy(ret.value);
        return [copied];
    case GRAB:
        copied = effect.copyTrigger(t, true);
        copied.grabs = [... copied.grabs, {
            trackNumber: ret.trackNumber,
            fromParam: ret.fromParam,
            toParam: ret.toParam,
            add: ret.add
        }];
        return [copied];
    case CHOKE:
        copied = effect.copyTrigger(t, true);
        copied.chokes = [... copied.chokes, {
            trackNumber: ret.trackNumber,
        }];
        return [copied];
    case GRAB_TIME:
        copied = effect.copyTrigger(t, true);
        copied.grabs = [... copied.grabs, {
            trackNumber: ret.trackNumber,
            isTime: true
        }];
        return [copied];
    case SEQUENTIALLY:
        for (let s of ret.sequence) {
            let {time, stepNumber} = t;
            if (Array.isArray(t)) {
                time = t[0].time;
                stepNumber = t[0].stepNumber;
            }
            let r = execute(s, Array.isArray(t) ? t : [t], stepNumber, time, effect);
            if (r.length === 0) {
                return [];
            }
            t = r;
        }
        return Array.isArray(t) ? t : [t];
    case PARALLEL:
        let trigs = [];
        for (let s of ret.sequence) {
            let r = _eval(s, t, effect);
            if (r.length === 0) {
                continue;
            }
            trigs = [...trigs, ...r];
        }
        return trigs;
    case SET_MODULATOR:
        setModulator(ret, effect.track.sequencerEngine);
        return [t];
    case SET_MACRO:
        setMacro(ret, effect.track);
        return [t];
    case PATTERN_SWITCH:
        patternSwitch(ret.value, effect.sequencerEngine || effect.track.sequencerEngine, effect.track);
        return [t];
    case TRIGGER:
        let li = [];
        if (Array.isArray(ret.value)) {
            return li = ret.value;
        } else { 
            li = [ret.value];
        }
        if (li.length === 0) {
            let stepData = effect.getStep(DEFAULT_STEP);
            stepData.track = effect.track;
            li = [{
                stepData: stepData,
                time: t.time,
                stepNumber: t.stepNumber
            }];
        }
        return li;
    case COND:
        // we must execute the condition
        let resolvedCondition = lazy(ret.condition, t.stepData, t.stepNumber, t.time, effect);
        // based on how it executed we do something
        let cond = typeof resolvedCondition === "object" ?
            resolvedCondition.value : resolvedCondition;
        let toExecute = cond ? ret.statement1 : ret.statement2;
        return _eval(toExecute, t, effect);
    case ON:
        if (t.message && t.message.type === ret.value.type) {
            // then we exceute
            let evaled = _eval(ret.value.fn(t.message.value), t, effect);
            return evaled;
        } else {
            return Array.isArray(t) ? t : [t];
        }
    }

    return [];
};

export const execute = (op, input, stepNumber, time, effect) => {
    let output = [];
    if (input.length > 0) {
        // there are input triggers to process
        for (let t of input) {
            // first pass the trigger information to the operation
            // and have it return the "compiled" results
            output = [... output, ... _eval(op, t, effect)];
       }
    } else {
        // there are no input triggers, however we must
        // check if there are any generated, nonetheless
        if (typeof op !== "function") {
            let _op = op;
            op = () => _op;
        }
        
        let ret  = op(undefined, stepNumber, time, effect);
        let {axiom, value} = ret;
        if (axiom === TRIGGER || axiom === PARALLEL || axiom == SEQUENTIALLY) {
            if (ret.sequence) {
                if (axiom === PARALLEL) { 
                    output = [
                        ... output,
                        ...ret.sequence.map(
                            x => execute(
                                x, input, stepNumber, time,  effect))
                            .reduce((a,b) => [...a, ...b], [])];
                } else {
                    output = [
                        ... output,
                        ...ret.sequence.reduce(
                            (acc, x) => execute(
                                x, acc, stepNumber, time, effect), input)
                    ];
                }
            }
            if (Array.isArray(value)) {
                output = [... output, ... value];
            } else if (value !== undefined) {
                output.push(value);
            }
            if (axiom === TRIGGER && output.length === 0) {
                let stepData = effect.getStep(DEFAULT_STEP);
                stepData.track = effect.track;
                output.push({
                    stepData: effect.getStep(DEFAULT_STEP),
                    time,
                    stepNumber
                });
            }
        }
    }
    return output;
};

const setModulator = ({number, modType, param, value}, sequencerEngine) =>  {
    if (modType === ENVELOPE) {
        let p = sequencerEngine.envelopes[number][param + 'Parameter'];
        if (p) {
            p.set(lazy(value));
        }
        
    } else {
        let p = sequencerEngine.lfos[number].lfo[param + 'Parameter'];
        if (p) {
            p.set(lazy(value));
        }
    }
};

const setMacro = ({name, value}, track) => {
    let macrosEffect = track.liveEffects.effects.find(
        e => e.name === "Macros");

    if (!macrosEffect) {
        return;
    }

    let macro = macrosEffect.macros.find(
        m => m.name === name);

    if (!macro) {
        return;
    }

    // don't let user choose huge values and go deaf
    let v = Math.max(0, Math.min(1, lazy(value)));
    macro.macroParameter.set(v);
};



const isMidiTrigger = (x) => {
    return x.stepData && (x.time !== undefined) && (x.stepNumber !== undefined);
};
